import { FormEvent, useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { ProviderFormValues, ProviderStatusValue } from "../types/providerIntegrationtypes";
import { generateRandomHashId } from "../../../utils/Utils";

export type AddProviderFormValues = ProviderFormValues;

interface AddProviderFormProps {
  onCancel: () => void;
  onSubmit: (values: AddProviderFormValues) => void;
  mode?: "add" | "edit";
  initialValues?: AddProviderFormValues;
  codeReadOnly?: boolean;
  submitLabel?: string;
  isSubmitting?: boolean;
  cancelDisabled?: boolean;
}

const STATUS_OPTIONS: ProviderStatusValue[] = [
  "Active",
  "Inactive",
];

const INITIAL_VALUES: AddProviderFormValues = {
  name: "",
  code: "",
  description: "",
  status: "Active",
  subscriptionKey: "",
};

const AddProviderForm = ({
  onCancel,
  onSubmit,
  mode = "add",
  initialValues,
  codeReadOnly = false,
  submitLabel,
  isSubmitting = false,
  cancelDisabled = false,
}: AddProviderFormProps) => {
  const [values, setValues] = useState<AddProviderFormValues>(() => {
    if (initialValues) return initialValues;
    if (mode !== "edit") {
      return {
        ...INITIAL_VALUES,
        subscriptionKey: generateRandomHashId(32),
      };
    }
    return INITIAL_VALUES;
  });
  const [errors, setErrors] = useState<{ name?: string; code?: string }>({});

  const isEdit = mode === "edit";

  const updateField = <K extends keyof AddProviderFormValues>(
    key: K,
    value: AddProviderFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (key === "name" || key === "code") {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  const handleGenerateId = () => {
    try {
      const id = generateRandomHashId(32);
      setValues((prev) => ({ ...prev, subscriptionKey: id }));
    } catch (error) {
      console.error("Failed to generate hash ID:", error);
    }
  };

  useEffect(() => {
    if (initialValues) {
      setValues(initialValues);
      setErrors({});
    }
  }, [initialValues]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const nextErrors: { name?: string; code?: string } = {};

    if (!values.name.trim()) {
      nextErrors.name = "Provider name is required.";
    }
    if (!isEdit && !values.code.trim()) {
      nextErrors.code = "Provider code / alias is required.";
    }

    if (nextErrors.name || nextErrors.code) {
      setErrors(nextErrors);
      return;
    }

    const subscriptionKey =
      values.subscriptionKey.trim() ||
      (!isEdit ? generateRandomHashId(32) : "");

    onSubmit({
      name: values.name.trim(),
      code: values.code.trim().toUpperCase(),
      description: values.description.trim(),
      status: values.status,
      subscriptionKey,
    });
  };

  return (
    <section
      className="ic-add-provider"
      aria-label={isEdit ? "Edit provider" : "Add provider"}
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="row g-3">
          <div className="col-md-4">
            <label htmlFor="ic-add-provider-name" className="form-label">
              Provider Name <span className="text-danger">*</span>
            </label>
            <input
              id="ic-add-provider-name"
              type="text"
              name="name"
              className={`form-control ${errors.name ? "is-invalid" : ""}`}
              placeholder="Enter provider name."
              value={values.name}
              onChange={(event) => updateField("name", event.target.value)}
              autoComplete="off"
            />
            {errors.name && (
              <div className="invalid-feedback d-block">{errors.name}</div>
            )}
          </div>

          <div className="col-md-4">
            <label htmlFor="ic-add-provider-code" className="form-label">
              Provider Code / Alias <span className="text-danger">*</span>
            </label>
            <input
              id="ic-add-provider-code"
              type="text"
              name="code"
              className={`form-control ${errors.code ? "is-invalid" : ""}`}
              placeholder="Enter code or alias."
              value={values.code}
              readOnly={codeReadOnly}
              disabled={codeReadOnly}
              onChange={(event) => updateField("code", event.target.value)}
              autoComplete="off"
            />
            {errors.code && (
              <div className="invalid-feedback d-block">{errors.code}</div>
            )}
          </div>

          <div className="col-md-4">
            <label htmlFor="ic-add-provider-status" className="form-label">
              Status
            </label>
            <select
              id="ic-add-provider-status"
              name="status"
              className="form-select form-control"
              value={values.status}
              onChange={(event) =>
                updateField(
                  "status",
                  event.target.value as AddProviderFormValues["status"],
                )
              }
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12">
            <label htmlFor="ic-add-provider-description" className="form-label">
              Description
            </label>
            <textarea
              id="ic-add-provider-description"
              name="description"
              className="form-control"
              rows={4}
              placeholder="Enter description (optional)."
              value={values.description}
              onChange={(event) =>
                updateField("description", event.target.value)
              }
            />
          </div>

          <div className="col-12">
            <label htmlFor="ic-add-provider-subscription-key" className="form-label">
              Subscription Key
            </label>
            <div className="ic-subscription-key-row">
              <input
                id="ic-add-provider-subscription-key"
                type="text"
                name="subscriptionKey"
                className="form-control"
                readOnly
                placeholder="Enter subscription key."
                value={values.subscriptionKey}
                onChange={(event) =>
                  updateField("subscriptionKey", event.target.value)
                }
                autoComplete="off"
                spellCheck={false}
              />
              <button
                type="button"
                className="btn btn-border-primary ic-subscription-key-btn"
                disabled={isSubmitting}
                data-tooltip-id="ic-generate-subscription-key-tooltip"
                data-tooltip-content="Generate Key"
                data-tooltip-place="top"
                aria-label="Generate Key"
                onClick={handleGenerateId}
              >
                <i className="fh_key_fill" aria-hidden="true" />
              </button>
              <Tooltip
                id="ic-generate-subscription-key-tooltip"
                place="top"
                positionStrategy="fixed"
                offset={8}
                className="ic-subscription-key-tooltip"
              />
            </div>
          </div>
        </div>

        <div className="d-flex flex-column flex-sm-row justify-content-center gap-2 mt-4 pt-3 border-top">
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {submitLabel || (isEdit ? "Save Changes" : "Save & Submit")}
          </button>
          <button
            type="button"
            className="btn btn-border-primary"
            onClick={onCancel}
            disabled={isSubmitting || cancelDisabled}
          >
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
};

export default AddProviderForm;
